import {NS} from '../namespaces.js';

const resources = {
  en: {
    [NS.FINANCE_ANALYSIS]: {
      title: 'Finance Analysis',
      subtitle: 'See what really shaped this period, what was kept separate, and why the numbers differ.',
      filters: {
        type: 'Transaction type',
        analysisMode: 'Analysis mode',
        typeAll: 'Income + spending',
        typeExpense: 'Expenses',
        typeIncome: 'Income',
        analysisModeOperating: 'Everyday view',
        analysisModeAccounting: 'Everyday + separate items',
        analysisModeRawLedger: 'Full ledger rows',
        categories: 'Categories',
        drilldownCategory: 'Drill-down: {{category}}',
        groups: 'Budget groups',
        accounts: 'Bank accounts',
        range: 'Date range',
        clear: 'Reset filters',
      },
      summary: {
        totalAmount: 'Flow total',
        incomeTotalAmount: 'Income total',
        expenseTotalAmount: 'Expense total',
        netAmount: 'Net flow',
        totalCount: 'Transactions',
        averageAmount: 'Average amount',
        uniqueCategories: 'Categories',
        estimatedCompatibility: 'Summary totals are estimated from compatibility base valuations.',
      },
      overview: {
        title: 'See the full picture for this period',
        subtitle: 'Start with every matching row, then separate everyday money from rows that should not distort that view.',
        ledgerNet: 'Net in this period',
        ledgerNetHint: 'This is the net result across everything in the current filters, including transfers, refunds, debt principal, corrections, and setup moves.',
        excludedMovement: 'Shown separately',
        excludedMovementHint: 'These rows were real activity in the ledger, but they do not belong in your normal income-and-spending picture.',
        operatingResult: 'Everyday net',
        operatingResultHint: 'What is left after removing rows that do not belong to normal day-to-day financial life.',
      },
      explanation: {
        title: 'Your everyday money view',
        subtitle: 'Use this view to budget and judge overspending. It keeps normal income and spending in, and keeps transfers, corrections, and similar rows from distorting the picture.',
        operatingSignal: 'Everyday net',
        operatingSignalHint: 'The result that best reflects everyday financial life in the selected period.',
        realIncome: 'Real income',
        realIncomeHint: 'Money that counts as real income for this period. Transfers, reimbursements, refunds, and similar pass-through money stay out.',
        realSpending: 'Real spending',
        realSpendingHint: 'Spending that truly reduced your everyday result in this period, including debt interest when it applies.',
        fixedEssentials: 'Bills and fixed commitments',
        fixedEssentialsHint: 'Regular costs you usually need to cover, like rent, insurance, subscriptions, and utilities.',
        variableEssentials: 'Essential spending that changes',
        variableEssentialsHint: 'Necessary spending that moves up and down, like groceries, transport, or medicine.',
        discretionary: 'Optional spending',
        discretionaryHint: 'Spending you could usually reduce, delay, or skip.',
        outsidePlanningSplit: 'Outside budget buckets',
        outsidePlanningSplitHint: 'These amounts still count as real spending, but they are not forced into fixed, essential, or optional buckets when that would be misleading.',
        alerts: {
          negativeNetReal: {
            title: 'You spent more than you earned in everyday life',
            body: 'After removing transfers, corrections, debt principal, and similar noise, everyday spending was still higher than everyday income in this scope.'
          },
          negativeNetDistorted: {
            title: 'The all-rows net looks worse than everyday life does',
            body: 'The ledger net is negative in this scope, but some of the drop comes from rows that are shown separately from normal day-to-day life.'
          },
          internalTransfer: {
            title: 'Transfers changed the all-rows net',
            body: 'Money moved between your own accounts. It changed the ledger total, but not your everyday spending.'
          },
          adjustment: {
            title: 'Balance fixes changed the all-rows net',
            body: 'Reconciliation and bookkeeping corrections changed the ledger total, but they are shown separately from everyday life.'
          },
          debtPrincipal: {
            title: 'Debt principal changed the all-rows net',
            body: 'Principal payments moved cash out, but they are financing moves, not normal spending.'
          },
          reimbursement: {
            title: 'Refunds, reimbursements, or paybacks changed the all-rows net',
            body: 'Some money coming in or out was a refund, reimbursement, or payback, not normal earned income.'
          },
          planningNeedsMapping: {
            title: 'Some real spending still needs a budget bucket',
            body: 'Only {{coverage}}% of the spending that should be split into budget buckets is mapped today. The rest still needs a fixed, essential, or optional bucket.'
          },
          planningOutsidePolicy: {
            title: 'Some real spending is intentionally shown outside the budget buckets',
            body: 'Some spending is real, but forcing it into fixed, essential, or optional buckets would be misleading. This can include debt interest or rare required one-offs.'
          },
          semanticReview: {
            title: 'Some rows still need your category choice',
            body: '{{count}} entries already have the right system meaning, but still need your category choice or an optional reports label.'
          },
        },
      },
      drilldown: {
        open: 'View transactions',
        detailsTitle: 'Transactions',
        totalRows: 'Transactions: {{count}}',
        totalAmount: 'Total:',
        empty: 'No transactions match this summary bucket.',
        loadError: 'Failed to load summary transactions.',
        earnedIncomeTitle: 'Real income',
        earnedIncomeDescription: 'These rows are counted as real income in this view.',
        realSpendingTitle: 'Real spending',
        realSpendingDescription: 'These rows are counted as spending that reduced everyday life in this view.',
        operatingExpenseTitle: 'Day-to-day spending',
        operatingExpenseDescription: 'These rows are counted as normal spending in the selected scope.',
        fixedEssentialTitle: 'Bills and fixed commitments',
        fixedEssentialDescription: 'These rows are grouped into bills and fixed commitments in this view.',
        variableEssentialTitle: 'Essential spending that changes',
        variableEssentialDescription: 'These rows are grouped into essential spending that changes in this view.',
        discretionaryTitle: 'Optional spending transactions',
        discretionaryDescription: 'These rows are grouped into optional spending in this view.',
      },
      chart: {
        title: 'Category breakdown',
        subtitle: 'The circular chart uses the full filtered dataset, not just the visible rows.',
        allTitle: 'Where the money went',
        allSubtitle: 'When transaction type is All, this chart separates everyday income, everyday spending, and money that should be shown separately.',
        pieTitle: 'Category breakdown',
        pieSubtitle: 'When transaction type is narrowed to income or expense, the circular chart shows top categories for the full filtered dataset.',
        empty: 'No category data for the current filters.',
        other: 'Other',
        otherBreakdown: 'Other includes {{count}} categories: {{categories}}.',
        all: {
          earnedIncome: 'Real income',
          operatingExpense: 'Real spending',
          excludedMovement: 'Shown separately',
        },
        countLabel: 'Count',
      },
      list: {
        title: 'Detailed transactions',
        empty: 'No transactions match the current filters.',
        columns: {
          date: 'Date',
          type: 'Type',
          meaning: 'Meaning',
          group: 'Budget group',
          account: 'Account',
          note: 'Note',
          amount: 'Amount',
        },
        valuation: {
          estimatedIn: 'Estimated in {{currency}}',
          pending: 'Revaluation pending',
          stale: 'Stale rate',
        },
        uncategorized: 'Uncategorized',
        ungrouped: 'No group',
        noNote: 'No note',
        meaning: {
          reviewNeeded: 'Needs review',
          systemInternalTransfer: 'Internal transfer',
          systemReconciliation: 'Reconciliation adjustment',
          systemDebtPrincipal: 'Debt principal',
          systemDebtInterest: 'Debt interest',
          systemGoalFunding: 'Goal funding',
          systemOpeningBalance: 'Opening balance',
          systemPeerTransfer: 'Peer transfer',
        },
        status: {
          businessMeaning: 'Business meaning',
          reportingOnly: 'Reporting only',
          systemOwned: 'System-owned',
          needsReview: 'Needs review',
        },
      },
      truth: {
        title: 'Shown separately',
        subtitle: 'These rows still belong to the selected filters. They are separated so your everyday income and spending stay clear.',
        excludedTitle: 'Money kept separate',
        reviewTitle: 'Needs your input',
        reviewAction: 'Open review queue',
        reviewHelp: 'The system already knows what these rows are. Use the review queue only to add missing category meaning or an optional reports label.',
        empty: 'Nothing to explain for the current filters.',
        excluded: {
          internalTransfer: {
            label: 'Transfers between your own accounts',
            hint: 'Real money movement, but not everyday income or spending.'
          },
          adjustment: {
            label: 'Balance fixes and corrections',
            hint: 'Ledger corrections that should not distort the everyday picture.'
          },
          financing: {
            label: 'Debt principal and financing moves',
            hint: 'Cash moved because of debt or liability principal, not because of normal spending.'
          },
          reimbursement: {
            label: 'Refunds, reimbursements, and paybacks',
            hint: 'Money that moved, but should not be mistaken for earned income or normal spending.'
          },
          peerTransfer: {
            label: 'Money from or to other people',
            hint: 'Peer-to-peer money that sits outside the everyday income view unless it is true earned income.'
          },
          goalFunding: {
            label: 'Goal funding',
            hint: 'Money moved into goals, not normal spending.'
          },
          openingBalance: {
            label: 'Starting balance setup',
            hint: 'Setup rows used to establish the ledger, not real period activity.'
          },
        },
        review: {
          countTag: '{{count}} rows',
          reconciliation: {
            label: 'Balance-fix rows still need an optional reports label',
            hint: 'The system already knows these are balance fixes. Only the optional reports label is still up to you.'
          },
          recurringExpense: {
            label: 'Recurring expenses still need a clearer category',
            hint: 'The system knows these are recurring expenses, but your category meaning is still missing.'
          },
          recurringIncome: {
            label: 'Recurring income still needs a clearer category',
            hint: 'The system knows these are recurring income rows, but your category meaning is still missing.'
          },
          manualEntry: {
            label: 'Some manual rows still need a clearer category',
            hint: 'The money movement is known, but your category meaning was never captured.'
          }
        }
      },
      chat: {
        title: 'AI chat on active filters',
        subtitle: 'The assistant works on the active filtered dataset and must distinguish full-scope analytics from the visible row slice.',
        placeholder: 'Ask about this filtered dataset',
        send: 'Send',
        sending: 'Sending…',
        empty: 'Ask a question about the active filtered result set.',
        reset: 'Filters changed. Start a new chat for the updated scope.',
        copy: 'Copy chat',
        download: 'Download chat',
        copyHint: 'Copy this filtered chat transcript.',
        downloadHint: 'Save this filtered chat transcript.',
        copySuccess: 'Chat copied',
        copyError: 'Failed to copy chat',
        downloadError: 'Failed to download chat',
        copyAnswer: 'Copy answer',
        copyAnswerSuccess: 'Answer copied',
        copyAnswerError: 'Failed to copy answer',
        credits: '{{count}} AI credits left',
        creditsUnknown: 'AI credits available',
        creditsEmpty: 'No AI credits left for this month.',
        accessDenied: 'Your current plan does not allow AI chat on this page.',
        providerUnavailable: 'AI assistant is temporarily unavailable.',
        sendError: 'Failed to send AI message.',
        emptyStateTitle: 'AI follows active filters',
        emptyStateText: 'Adjust the filters above, then ask questions about the exact same filtered transaction scope.',
        historySaved: 'Saved chats live in History, where you can reopen and continue the thread later.',
        openHistory: 'Open history',
        continueLatest: 'Continue latest thread',
        latestQuestion: 'Latest question',
        latestAnswer: 'Latest answer',
        historyHint: 'Use History to reopen older threads or continue this one after refresh.',
        stale: 'Generated for older filters',
        rerun: 'Run again on current filters',
        transcript: {
          scopeLabel: 'Scope',
          userLabel: 'User',
          assistantLabel: 'Assistant',
        },
        context: {
          type: 'Type',
          analysisMode: 'Analysis mode',
          categories: 'Categories',
          analyticalDrilldowns: 'Analytical drill-downs',
          groups: 'Groups',
          accounts: 'Accounts',
          dateRange: 'Date range',
          fullFiltered: 'Full filtered transactions',
          visibleRows: 'Visible rows',
          truncated: 'Visible slice truncated',
          total: 'Total',
          yes: 'yes',
          no: 'no',
        },
        quickPrompts: {
          leaks: 'Where is money leaking?',
          cut: 'What can I cut this month?',
          recurring: 'Audit recurring payments',
          unusual: 'Show unusual categories',
          structure: 'Explain income vs expense structure',
        },
      },
      history: {
        title: 'Finance Analysis History',
        description: 'Review saved finance-analysis chats and continue the ones that still matter.',
        listTitle: 'Saved finance-analysis threads',
        listDescription: '{{count}} saved threads',
        empty: 'No saved finance-analysis chats yet.',
        openDetails: 'Open thread',
        loadError: 'Failed to load finance-analysis history.',
        meta: '{{createdAt}} · {{type}} · {{analysisMode}} · {{total}} rows',
        scope: {
          all: 'All flows',
          operating: 'Operating',
          accounting: 'Operating + excluded',
          rawLedger: 'Full ledger',
          rows: '{{count}} rows',
          categories: '{{count}} categories',
          groups: '{{count}} groups',
          accounts: '{{count}} accounts',
          drilldowns: '{{count}} drill-downs',
          summary: '{{categories}} categories · {{groups}} groups · {{accounts}} accounts · {{drilldowns}} drill-downs',
          openSnapshot: 'Open analysis snapshot'
        },
        detail: {
          title: 'Finance Analysis Thread',
          description: 'Continue this chat or reopen the same analysis snapshot.',
          back: 'Back to history',
          loadError: 'Failed to load finance-analysis thread.',
          createdAt: 'Created',
          type: 'Type',
          analysisMode: 'Analysis mode',
          dateRange: 'Date range',
          totalFiltered: 'Filtered rows',
          summaryTitle: 'Saved snapshot',
          summaryDescription: 'This thread keeps the filters and summary snapshot that were active when the chat started.',
          scopeNote: 'Open analysis snapshot to see the same filter scope on the live Finance Analysis page.',
          questionLabel: 'Opening question',
          answerLabel: 'Opening answer',
          chatTitle: 'Continue chat',
          chatDescription: 'Ask follow-up questions against this saved finance-analysis scope. New replies stay attached to this thread in History.',
          chatLoadError: 'Failed to load chat messages.',
          chatPlaceholder: 'Ask a follow-up about this saved finance-analysis scope',
          chatSend: 'Send',
          chatSending: 'Sending…',
          chatTyping: 'Thinking…',
          chatError: 'Failed to send follow-up message.',
          chatCreditsEmpty: 'No AI credits left for this month.',
          chatCopy: 'Copy answer',
          chatCopySuccess: 'Answer copied',
          chatCopyError: 'Failed to copy answer',
          providerUnavailableInline: 'AI assistant is temporarily unavailable.',
          export: {
            copy: 'Copy chat',
            copyHint: 'Copy this saved finance-analysis chat transcript.',
            copySuccess: 'Chat copied',
            copyError: 'Failed to copy chat',
            download: 'Download chat',
            downloadHint: 'Save this saved finance-analysis chat transcript.',
            downloadError: 'Failed to download chat',
            contextLabel: 'Scope',
            userLabel: 'User',
            assistantLabel: 'Assistant'
          }
        }
      },
      scope: {
        fullDatasetCount: 'Full filtered transactions: {{count}}',
        visibleSliceCount: 'Visible rendered rows: {{count}}',
        truncated: 'List is truncated',
        notTruncated: 'Full list is visible',
        analysisModeValue: 'Mode: {{mode}}',
        ladderTitle: 'How this page was filtered',
        ledgerScopeCount: 'All matching ledger rows: {{count}}',
        typeScopeCount: 'After the income / spending filter: {{count}}',
        modeScopeCount: 'Shown in this analysis mode: {{count}}',
        excludedByTypeCount: 'Left out by the income / spending filter: {{count}}',
        excludedByModeCount: 'Shown separately by this analysis mode: {{count}}',
        typeAllHint: 'This view keeps income and spending rows. Transfer rows stay out here.',
        operatingHint: 'Everyday mode keeps only the income and spending that belong to normal day-to-day life.',
        accountingHint: 'Expanded mode also keeps financing moves, peer transfers, goal funding, and corrections.',
        rawLedgerHint: 'Full ledger rows keep every row that survived the current transaction type filter.',
        summaryAndChart: 'Totals and chart use the full filtered dataset.',
        tableAndChat: 'The table shows the visible slice; AI also receives that slice as supplemental context.',
        chartFullDataset: 'Chart and totals are computed from the full filtered dataset.',
        chatUsesFullDataset: 'AI sees the full filtered dataset summary for {{total}} transactions and the current visible slice of {{visible}} rows.',
        chatVisibleSliceWarning: 'Visible-row patterns are only a slice. AI should not generalize them to the whole dataset unless the full-scope summary supports it.',
        tableVisibleSlice: 'Showing {{visible}} rows on this page out of {{total}} rows included by the current analysis mode.',
      },
      states: {
        loadError: 'Failed to load finance analysis.',
        filterLoadError: 'Failed to load filter options.',
        noFilters: 'Try changing categories, date range, groups, accounts, or transaction type.',
      },
      type: {
        expense: 'Expense',
        income: 'Income',
        transfer: 'Transfer',
      },
    },
  },
  uk: {
    [NS.FINANCE_ANALYSIS]: {
      title: 'Фінансовий аналіз',
      subtitle: 'Досліджуйте той самий відфільтрований набір транзакцій через підсумки, діаграму, детальний список і AI-чат.',
      filters: {
        type: 'Тип транзакції',
        analysisMode: 'Режим аналізу',
        typeAll: 'Дохід + витрати',
        typeExpense: 'Витрати',
        typeIncome: 'Дохід',
        analysisModeOperating: 'Операційний cashflow',
        analysisModeAccounting: 'Операційний + виключений',
        analysisModeRawLedger: 'Усі ledger-рядки',
        categories: 'Категорії',
        drilldownCategory: 'Drill-down: {{category}}',
        groups: 'Бюджетні групи',
        accounts: 'Банківські рахунки',
        range: 'Діапазон дат',
        clear: 'Скинути фільтри',
      },
      summary: {
        totalAmount: 'Сумарний потік',
        incomeTotalAmount: 'Сума доходів',
        expenseTotalAmount: 'Сума витрат',
        netAmount: 'Чистий потік',
        totalCount: 'Транзакції',
        averageAmount: 'Середня сума',
        uniqueCategories: 'Категорії',
        estimatedCompatibility: 'Підсумкові суми оцінені з compatibility base valuation.',
      },
      overview: {
        title: 'Що сталося у цьому scope',
        subtitle: 'Почніть з усього, що є в ledger, а потім відокремте рядки, які не належать до повсякденного planning view.',
        ledgerNet: 'Результат по всіх рядках',
        ledgerNetHint: 'Чистий результат по всіх рядках, що пережили поточні фільтри за датою, рахунком і категорією.',
        excludedMovement: 'Поза повсякденним view',
        excludedMovementHint: 'Перекази, debt principal, reconciliation, goal funding, refunds та інші рядки, які лишаються в scope, але не належать до звичайного повсякденного cashflow.',
        operatingResult: 'Повсякденний результат',
        operatingResultHint: 'Що лишається після виключення рядків, які не належать до нормального повсякденного cashflow.',
      },
      explanation: {
        title: 'Повсякденний planning view',
        subtitle: 'Сприймайте це як один planning lens того самого scope, а не як єдину правду сторінки.',
        operatingSignal: 'Повсякденний результат',
        operatingSignalHint: 'Результат після виключення рядків поза нормальним повсякденним cashflow.',
        realIncome: 'Зароблений дохід',
        realIncomeHint: 'Дохід, який вважається нормальним earned inflow у цьому scope.',
        realSpending: 'Повсякденні витрати',
        realSpendingHint: 'Операційні витрати та debt interest, які реально зменшують результат у цьому scope.',
        fixedEssentials: 'Фіксовані зобовʼязання',
        fixedEssentialsHint: 'Регулярні зобовʼязання на кшталт оренди, страхування, комунальних та інших fixed essentials.',
        variableEssentials: 'Змінні необхідні витрати',
        variableEssentialsHint: 'Необхідні витрати, що змінюються від місяця до місяця: продукти, транспорт або медицина.',
        discretionary: 'Необовʼязкові витрати',
        discretionaryHint: 'Необовʼязкові витрати у цьому scope.',
        outsidePlanningSplit: 'Поза planning split',
        outsidePlanningSplitHint: 'Реальні витрати, що лишаються у результаті, але не входять до fixed, variable або discretionary planning buckets.',
        alerts: {
          negativeNetReal: {
            title: 'Негативний результат відображає реальне перевитрачання у цьому scope',
            body: 'Реальні витрати перевищили зароблений дохід у вибраному scope після виключення transfer noise та бухгалтерських коригувань.'
          },
          negativeNetDistorted: {
            title: 'Негативний результат частково викликаний таймінгом або класифікацією',
            body: 'Ledger net негативний у вибраному scope, але частину просідання пояснюють неопераційні елементи.'
          },
          internalTransfer: {
            title: 'Внутрішні перекази вплинули на сирий ledger',
            body: 'Гроші переміщалися між рахунками і не повинні читатися як реальні витрати.'
          },
          adjustment: {
            title: 'У цьому періоді є коригування',
            body: 'Звірка або бухгалтерські коригування вплинули на ledger totals.'
          },
          debtPrincipal: {
            title: 'Основна сума боргу зменшила вибраний scope',
            body: 'Платежі по основній сумі боргу є фінансовими outflow, а не звичайними витратами.'
          },
          reimbursement: {
            title: 'Відшкодування або refunds вплинули на надходження',
            body: 'Частина вхідного cash може не бути заробленим доходом для планування.'
          },
          planningNeedsMapping: {
            title: 'Частині повсякденних витрат ще бракує planning bucket',
            body: 'Лише {{coverage}}% planning-eligible витрат розкладено на fixed, variable або discretionary buckets. Решта ще потребує mapping.'
          },
          planningOutsidePolicy: {
            title: 'Частина реальних витрат свідомо лишається поза planning split',
            body: 'Debt interest, рідкісні required one-offs або policy-defined витрати можуть впливати на результат без примусового віднесення до fixed, variable або discretionary buckets.'
          },
          semanticReview: {
            title: 'Деякі транзакції ще потребують семантичного ревʼю',
            body: '{{count}} записів ще не мають user-owned business meaning або reconciliation annotation.'
          },
        },
      },
      drilldown: {
        open: 'Переглянути транзакції',
        detailsTitle: 'Транзакції',
        totalRows: 'Транзакції: {{count}}',
        totalAmount: 'Сума:',
        empty: 'Для цього summary bucket немає транзакцій.',
        loadError: 'Не вдалося завантажити транзакції для цього summary bucket.',
        earnedIncomeTitle: 'Транзакції заробленого доходу',
        earnedIncomeDescription: 'Ці рядки трактуються як earned income у поточному finance analysis scope.',
        realSpendingTitle: 'Транзакції реальних витрат',
        realSpendingDescription: 'Ці рядки трактуються як витрати, що реально зменшують повсякденний результат у поточному finance analysis scope.',
        operatingExpenseTitle: 'Транзакції повсякденних витрат',
        operatingExpenseDescription: 'Ці рядки трактуються як нормальні повсякденні витрати у поточному finance analysis scope.',
        fixedEssentialTitle: 'Транзакції фіксованих зобовʼязань',
        fixedEssentialDescription: 'Ці рядки згруповано у fixed commitments у поточному planning view.',
        variableEssentialTitle: 'Транзакції змінних необхідних витрат',
        variableEssentialDescription: 'Ці рядки згруповано у variable essentials у поточному planning view.',
        discretionaryTitle: 'Транзакції необовʼязкових витрат',
        discretionaryDescription: 'Ці рядки згруповано у optional spending у поточному planning view.',
      },
      chart: {
        title: 'Структура за категоріями',
        subtitle: 'Кругова діаграма використовує весь відфільтрований набір, а не лише видимі рядки.',
        allTitle: 'Структура потоку',
        allSubtitle: 'Коли тип транзакції = Усі, ця діаграма відокремлює earned income, real spending і excluded movement для всього відфільтрованого набору.',
        pieTitle: 'Структура за категоріями',
        pieSubtitle: 'Коли тип транзакції звужено до доходу або витрат, кругова діаграма показує top categories для повного відфільтрованого набору.',
        empty: 'Для поточних фільтрів немає даних за категоріями.',
        other: 'Інше',
        otherBreakdown: 'У «Інше» входить {{count}} категорій: {{categories}}.',
        all: {
          earnedIncome: 'Earned income',
          operatingExpense: 'Real spending',
          excludedMovement: 'Поза повсякденним view',
        },
        countLabel: 'Кількість',
      },
      list: {
        title: 'Детальні транзакції',
        empty: 'Жодна транзакція не відповідає поточним фільтрам.',
        columns: {
          date: 'Дата',
          type: 'Тип',
          meaning: 'Зміст',
          group: 'Бюджетна група',
          account: 'Рахунок',
          note: 'Нотатка',
          amount: 'Сума',
        },
        valuation: {
          estimatedIn: 'Оцінка в {{currency}}',
          pending: 'Очікується переоцінка',
          stale: 'Застарілий курс',
        },
        uncategorized: 'Без категорії',
        ungrouped: 'Без групи',
        noNote: 'Без нотатки',
        meaning: {
          reviewNeeded: 'Потребує ревʼю',
          systemInternalTransfer: 'Внутрішній переказ',
          systemReconciliation: 'Коригування звірки',
          systemDebtPrincipal: 'Основна сума боргу',
          systemDebtInterest: 'Відсотки за боргом',
          systemGoalFunding: 'Поповнення цілі',
          systemOpeningBalance: 'Початковий баланс',
          systemPeerTransfer: 'Peer transfer',
        },
        status: {
          businessMeaning: 'Business meaning',
          reportingOnly: 'Лише для звітності',
          systemOwned: 'System-owned',
          needsReview: 'Потребує ревʼю',
        },
      },
      truth: {
        title: 'Що лежить поза повсякденним view',
        subtitle: 'Ці рядки все ще всередині вибраного date, account і category scope, але вони не належать до нормального повсякденного cashflow.',
        excludedTitle: 'Рядки поза повсякденним view',
        reviewTitle: 'Ще потребує сенсу категорії',
        reviewAction: 'Відкрити review queue',
        reviewHelp: 'У цих рядків system meaning вже правильний. Відкрийте review queue лише щоб додати відсутній user-owned meaning або optional reporting annotation.',
        empty: 'Для поточних фільтрів тут нічого показувати.',
        excluded: {
          internalTransfer: {
            label: 'Внутрішні перекази',
            hint: 'Гроші переміщалися між власними рахунками.'
          },
          adjustment: {
            label: 'Коригування звірки',
            hint: 'Бухгалтерські виправлення та балансні корекції.'
          },
          financing: {
            label: 'Основна сума боргу та liabilities',
            hint: 'Фінансові рухи, а не звичайні витрати.'
          },
          reimbursement: {
            label: 'Відшкодування та refunds',
            hint: 'Гроші, які не повинні роздувати earned income.'
          },
          peerTransfer: {
            label: 'Peer transfers',
            hint: 'Peer-to-peer гроші поза normal operating cashflow.'
          },
          goalFunding: {
            label: 'Поповнення цілей',
            hint: 'Гроші, переведені в goals, а не звичайні витрати.'
          },
          openingBalance: {
            label: 'Рухи початкового балансу',
            hint: 'Початкове налаштування ledger, а не реальний cashflow періоду.'
          },
        },
        review: {
          countTag: '{{count}} рядків',
          reconciliation: {
            label: 'Рядки reconciliation ще потребують optional annotation',
            hint: 'System reconciliation вже відома; reporting annotation усе ще user-owned.'
          },
          recurringExpense: {
            label: 'Recurring expense rows ще потребують business meaning',
            hint: 'Повторюваність відома, але business category ще відсутня.'
          },
          recurringIncome: {
            label: 'Recurring income rows ще потребують business meaning',
            hint: 'Повторюваність відома, але зміст доходу ще не заданий.'
          },
          manualEntry: {
            label: 'Manual cashflow rows ще потребують business meaning',
            hint: 'Рух грошей відомий, але user-owned meaning ніколи не був явно збережений.'
          }
        }
      },
      chat: {
        title: 'AI-чат за активними фільтрами',
        subtitle: 'Асистент працює з активним відфільтрованим набором і має відрізняти повний зріз від видимої частини списку.',
        placeholder: 'Поставте запитання про цей відфільтрований набір даних',
        send: 'Надіслати',
        sending: 'Надсилання…',
        empty: 'Поставте запитання про активний відфільтрований набір результатів.',
        reset: 'Фільтри змінено. Почніть новий чат для оновленого набору.',
        copy: 'Скопіювати чат',
        download: 'Завантажити чат',
        copyHint: 'Скопіювати стенограму цього відфільтрованого чату.',
        downloadHint: 'Зберегти стенограму цього відфільтрованого чату.',
        copySuccess: 'Чат скопійовано',
        copyError: 'Не вдалося скопіювати чат',
        downloadError: 'Не вдалося завантажити чат',
        copyAnswer: 'Скопіювати відповідь',
        copyAnswerSuccess: 'Відповідь скопійовано',
        copyAnswerError: 'Не вдалося скопіювати відповідь',
        credits: 'Залишилось AI-кредитів: {{count}}',
        creditsUnknown: 'AI-кредити доступні',
        creditsEmpty: 'На цей місяць AI-кредити закінчилися.',
        accessDenied: 'Ваш поточний план не дозволяє AI-чат на цій сторінці.',
        providerUnavailable: 'AI-асистент тимчасово недоступний.',
        sendError: 'Не вдалося надіслати AI-повідомлення.',
        emptyStateTitle: 'AI слідує активним фільтрам',
        emptyStateText: 'Змініть фільтри вище, а потім ставте запитання про точно той самий відфільтрований набір транзакцій.',
        historySaved: 'Збережені чати живуть в History, де можна відкрити й продовжити тред пізніше.',
        openHistory: 'Відкрити history',
        continueLatest: 'Продовжити останній тред',
        latestQuestion: 'Останнє запитання',
        latestAnswer: 'Остання відповідь',
        historyHint: 'Використовуйте History, щоб відкрити старі треди або продовжити цей після refresh.',
        stale: 'Згенеровано для старих фільтрів',
        rerun: 'Запустити знову для поточних фільтрів',
        transcript: {
          scopeLabel: 'Scope',
          userLabel: 'User',
          assistantLabel: 'Assistant',
        },
        context: {
          type: 'Тип',
          analysisMode: 'Режим аналізу',
          categories: 'Категорії',
          analyticalDrilldowns: 'Аналітичні drill-downs',
          groups: 'Групи',
          accounts: 'Рахунки',
          dateRange: 'Діапазон дат',
          fullFiltered: 'Усі відфільтровані транзакції',
          visibleRows: 'Видимі рядки',
          truncated: 'Видимий slice обрізано',
          total: 'Разом',
          yes: 'так',
          no: 'ні',
        },
        quickPrompts: {
          leaks: 'Де витікають гроші?',
          cut: 'Що я можу скоротити цього місяця?',
          recurring: 'Перевір регулярні платежі',
          unusual: 'Покажи незвичні категорії',
          structure: 'Поясни структуру доходів і витрат',
        },
      },
      history: {
        title: 'Історія Finance Analysis',
        description: 'Переглядайте збережені чати finance-analysis і продовжуйте ті, що ще мають значення.',
        listTitle: 'Збережені треди finance-analysis',
        listDescription: '{{count}} збережених тредів',
        empty: 'Збережених чатів finance-analysis ще немає.',
        openDetails: 'Відкрити тред',
        loadError: 'Не вдалося завантажити history finance-analysis.',
        meta: '{{createdAt}} · {{type}} · {{analysisMode}} · {{total}} рядків',
        scope: {
          all: 'Усі потоки',
          operating: 'Операційний',
          accounting: 'Операційний + виключене',
          rawLedger: 'Повний ledger',
          rows: '{{count}} рядків',
          categories: '{{count}} категорій',
          groups: '{{count}} груп',
          accounts: '{{count}} рахунків',
          drilldowns: '{{count}} drill-downів',
          summary: '{{categories}} категорій · {{groups}} груп · {{accounts}} рахунків · {{drilldowns}} drill-downів',
          openSnapshot: 'Відкрити analysis snapshot'
        },
        detail: {
          title: 'Тред Finance Analysis',
          description: 'Продовжуйте цей чат або знову відкрийте той самий analysis snapshot.',
          back: 'Назад до history',
          loadError: 'Не вдалося завантажити тред finance-analysis.',
          createdAt: 'Створено',
          type: 'Тип',
          analysisMode: 'Режим аналізу',
          dateRange: 'Діапазон дат',
          totalFiltered: 'Відфільтровані рядки',
          summaryTitle: 'Збережений snapshot',
          summaryDescription: 'Цей тред зберігає фільтри та summary snapshot, які були активні під час старту чату.',
          scopeNote: 'Відкрийте analysis snapshot, щоб побачити той самий filter scope на живій сторінці Finance Analysis.',
          questionLabel: 'Початкове запитання',
          answerLabel: 'Початкова відповідь',
          chatTitle: 'Продовжити чат',
          chatDescription: 'Ставте follow-up запитання на основі цього збереженого scope finance-analysis. Нові відповіді залишаються прикріпленими до цього треду в History.',
          chatLoadError: 'Не вдалося завантажити повідомлення чату.',
          chatPlaceholder: 'Поставте follow-up запитання про цей збережений scope finance-analysis',
          chatSend: 'Надіслати',
          chatSending: 'Надсилання…',
          chatTyping: 'Думаю…',
          chatError: 'Не вдалося надіслати follow-up повідомлення.',
          chatCreditsEmpty: 'На цей місяць AI-кредити закінчилися.',
          chatCopy: 'Скопіювати відповідь',
          chatCopySuccess: 'Відповідь скопійовано',
          chatCopyError: 'Не вдалося скопіювати відповідь',
          providerUnavailableInline: 'AI-асистент тимчасово недоступний.',
          export: {
            copy: 'Скопіювати чат',
            copyHint: 'Скопіювати transcript цього збереженого чату finance-analysis.',
            copySuccess: 'Чат скопійовано',
            copyError: 'Не вдалося скопіювати чат',
            download: 'Завантажити чат',
            downloadHint: 'Зберегти transcript цього збереженого чату finance-analysis.',
            downloadError: 'Не вдалося завантажити чат',
            contextLabel: 'Scope',
            userLabel: 'User',
            assistantLabel: 'Assistant'
          }
        }
      },
      scope: {
        fullDatasetCount: 'Усього відфільтрованих транзакцій: {{count}}',
        visibleSliceCount: 'Видимих рядків: {{count}}',
        truncated: 'Список скорочено',
        notTruncated: 'Увесь список видимий',
        analysisModeValue: 'Режим: {{mode}}',
        ladderTitle: 'Як побудовано цю таблицю',
        ledgerScopeCount: 'Усі ledger-рядки в цьому scope за датами, рахунками і категоріями: {{count}}',
        typeScopeCount: 'Після фільтра типу транзакції: {{count}}',
        modeScopeCount: 'У поточному режимі аналізу: {{count}}',
        excludedByTypeCount: 'Виключено фільтром типу: {{count}}',
        excludedByModeCount: 'Виключено режимом аналізу: {{count}}',
        typeAllHint: 'Цей тип транзакції залишає лише дохідні та витратні рядки. Transfer-рядки тут виключені.',
        operatingHint: 'Операційний cashflow залишає лише рядки, що трактуються як нормальний повсякденний дохід і витрати.',
        accountingHint: 'Операційний + виключений також залишає financing, peer transfers, goal funding і adjustments.',
        rawLedgerHint: 'Усі ledger-рядки залишають кожен рядок, що пережив поточний фільтр типу транзакції.',
        summaryAndChart: 'Підсумки та діаграма використовують увесь відфільтрований набір.',
        tableAndChat: 'Таблиця показує видимий зріз; AI також отримує цей зріз як додатковий контекст.',
        chartFullDataset: 'Діаграма та підсумки обчислюються з усього відфільтрованого набору.',
        chatUsesFullDataset: 'AI бачить підсумок повного відфільтрованого набору для {{total}} транзакцій і поточний видимий зріз з {{visible}} рядків.',
        chatVisibleSliceWarning: 'Патерни у видимих рядках є лише зрізом. AI не повинен узагальнювати їх на весь набір без підтвердження у повному зведенні.',
        tableVisibleSlice: 'Показано {{visible}} рядків на цій сторінці із {{total}} рядків, що входять у поточний analysis mode.',
      },
      states: {
        loadError: 'Не вдалося завантажити фінансовий аналіз.',
        filterLoadError: 'Не вдалося завантажити опції фільтрів.',
        noFilters: 'Спробуйте змінити категорії, діапазон дат, групи, рахунки або тип транзакції.',
      },
      type: {
        expense: 'Витрата',
        income: 'Дохід',
        transfer: 'Переказ',
      },
    },
  },
};

export default resources;
